import React, { useState, useEffect, useMemo, useContext } from 'react';
import './style.css';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { Wrench, CirclePercent, ChevronLeft, Tag } from 'lucide-react';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import { WindowSizeContext } from '/src/App';

const HouseView = ({ handleProjectClick, propertyAreasWithProjects, projects }) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Image variables
    const new_hub_house_image_filename = "house-lansdcape.webp";
    const new_hub_house_image_url = CreateCloudfrontUrl(new_hub_house_image_filename);

    // Create unique house areas from projects
    const houseAreas = useMemo(() => {
        const uniqueAreas = new Map();

        // Add ALL option first
        uniqueAreas.set('all', {
            project_home_category_id: 'all',
            project_home_category: 'All Areas',
            house_category_icon: 'Home-Black.png' // You might want to adjust this icon
        });

        // Add other areas
        projects.forEach(project => {
            const id = project.project_home_category_id;
            if (!uniqueAreas.has(id)) {
                uniqueAreas.set(id, {
                    project_home_category_id: id,
                    project_home_category: project.project_home_category,
                    house_category_icon: project.house_category_icon
                });
            }
        });

        // Convert to array and sort alphabetically by category name, keeping 'All Areas' first
        return Array.from(uniqueAreas.values()).sort((a, b) => {
            if (a.project_home_category === 'All Areas') return -1;
            if (b.project_home_category === 'All Areas') return 1;
            return a.project_home_category.localeCompare(b.project_home_category);
        });
    }, [projects]);


    const [selectedFilter, setSelectedFilter] = useState('all');
    const [completePropertyAreasWithProjects, setCompletePropertyAreasWithProjects] = useState(null);
    const [selectedArea, setSelectedArea] = useState(null);
    const [selectedAreaProjects, setSelectedAreaProjects] = useState(null);

    // useEffect to run once at mount and find the area with the most projects and set that as the selected area
    useEffect(() => {
        if (propertyAreasWithProjects && projects) {

            // Add ALL PROJECTS to the property

        }
    }, [propertyAreasWithProjects]);

    const ALL_PROJECTS_ID = '99'

    // useEffect to run once at mount and find the area with the most projects and set that as the selected area
    useEffect(() => {
        if (propertyAreasWithProjects) {
            const areaWithMostProjects = propertyAreasWithProjects.reduce((max, current) => {
                return current.num_projects > max.num_projects ? current : max;
            }, propertyAreasWithProjects[0]);

            // Add ALL PROJECTS to the propertyAreasWithProjects
            const propertyAreasWithProjectsComplete = [...propertyAreasWithProjects, {
                id: ALL_PROJECTS_ID,
                category: 'See All Projects',
                projects_list: projects,
                risk_level_adjusted: 'low',
            }];

            setCompletePropertyAreasWithProjects(propertyAreasWithProjectsComplete);
            setSelectedArea(areaWithMostProjects);
            setSelectedAreaProjects(areaWithMostProjects.projects_list);
        }
    }, [propertyAreasWithProjects]);

    // Create a handler that updates the selected area when a new area is clicked (or to null if the same area is clicked again)
    const handleAreaClick = (areaId) => {

        setSelectedFilter('all');

        // If the areaId is null, set the selected area to null
        if (areaId === null) {
            setSelectedArea(null);
            return;
        }
        // Get the area from the propertyAreasWithProjects array
        const area = completePropertyAreasWithProjects.find(area => area.id === areaId);

        // If selectedArea.id is not the same as areaId, set the selected area to the area
        if (selectedArea?.id !== areaId) {
            setSelectedArea(area);
            // setSelectedAreaProjects(area.projects_list);
        }

        // If selectedArea.id is the same as areaId, set the selected area to null
        else {
            setSelectedArea(null);
        }
    };

    // Filter and sort projects based on selection
    useEffect(() => {
        if (selectedArea) {
            let filtered = selectedFilter === 'all'
                ? selectedArea.projects_list
                : selectedArea.projects_list.filter(project => project.project_home_category_id === selectedFilter);

            // Sort by project_total_payback in descending order
            filtered.sort((a, b) => b.project_total_payback - a.project_total_payback);
            setSelectedAreaProjects(filtered);
        }
    }, [selectedArea, selectedFilter]);

    // Map numeric IDs to house area classes based on the actual data
    const getAreaClass = (id) => {
        const areaMap = {
            '2': 'roof',           // First object in array has id '2'
            '1': 'landscaping',    // Second object has id '1'
            '6': 'windows',        // Third object has id '6'
            '10': 'hvac',          // Fourth object has id '10'
            '7': 'doors',          // Fifth object has id '7'
            '14': 'frame',         // Sixth object has id '14'
            '4': 'foundation',     // Seventh object has id '4'
            '9': 'siding',         // Eighth object has id '9'
            '5': 'plumbing',       // Ninth object has id '5'
            '13': 'basement',      // Tenth object has id '13',
            '99': 'all-projects'   // Tenth object has id '13',
        };
        return areaMap[id] || 'unknown';
    };

    // Get the color for the risk level
    const getRiskColor = (risk_level) => {
        switch (risk_level.toLowerCase()) {
            case 'high': return 'var(--related_red)';
            case 'medium': return 'var(--yellow)';
            case 'low': return 'var(--light-grey)';
            default: return 'var(--light-grey)';
        }
    };

    // Filter the propertyAreas with projects array to only include areas that have projects
    // const areasWithProjects = propertyAreasWithProjects.filter(area => area.num_projects > 0);

    const selectPartOfHomeText = "Select an area of the home to see its recommended projects."

    return (

        <>
            <div className={`view-projects__home-hub-container recommendations-view ${deviceType}`}>
                <img
                    className="view-projects__home-hub-image"
                    src={new_hub_house_image_url}
                    alt="House diagram"
                />
                <div className={`view-projects__house-area-markers ${deviceType}`}>
                    {completePropertyAreasWithProjects?.map((area) => {
                        const areaId = area.id;
                        const areThereProjects = area.num_projects > 0;
                        const risk_level = area.risk_level_adjusted || area.risk_level;
                        return (
                            <div
                                key={area.id}
                                className={`
                                    ${(areThereProjects || areaId === ALL_PROJECTS_ID) ? 'clickable' : 'not-clickable'} 
                                    ${areaId !== ALL_PROJECTS_ID && 'house-project-marker-container'}
                                    ${(areaId !== ALL_PROJECTS_ID && selectedArea?.id === ALL_PROJECTS_ID) && 'opaque'}
                                    house-risk-marker-${getAreaClass(areaId)} 
                                    ${selectedArea?.id === area.id ? 'selected' : ''} ${risk_level}
                                    ${deviceType}
                                    `}
                                onClick={(areThereProjects || areaId === ALL_PROJECTS_ID) ? () => handleAreaClick(area.id) : null}
                            >
                                {(deviceType === 'desktop' || areaId === ALL_PROJECTS_ID) && (
                                    <>
                                        <div className={`house-risk-marker-header ${deviceType}`}>
                                            <span className="house-risk-marker-header-text">{area.category}</span>
                                        </div>
                                        {
                                            areThereProjects && (
                                                <div className="house-risk-marker-body">
                                                    <Wrench size={16} />
                                                    <span className="house-risk-marker-body-text"> {area.num_projects}</span>
                                                    <Tag size={16} />
                                                    <span className="house-risk-marker-body-text">{area.num_discounts}</span>
                                                </div>
                                            )
                                        }
                                    </>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
            {!selectedArea &&
                <>
                    <p className="view-projects__select-part-of-home-text text-align-center">{selectPartOfHomeText}</p>
                </>
            }
            {selectedArea &&
                <>
                    <div className="selected-area-summary">
                        <CurlyBraceDivider direction="down" color="grey" />
                        <div className={`selected-area-info-container ${(selectedArea.id === ALL_PROJECTS_ID) ? 'all-projects' : selectedArea.risk_level_adjusted}`}>
                            <span className="selected-area-info-container-text">{selectedArea.category}</span>
                        </div>
                        {(selectedArea.id === ALL_PROJECTS_ID) && (
                            <div className={`view-projects__all-projects-filter-section ${deviceType}`}>
                                <span className='view-projects__all-projects-filter-section-label'>Filter by:</span>
                                <div className={`view-projects__all-projects-filter-bar ${deviceType}`}>
                                    {houseAreas.map(area => (
                                        <button
                                            key={area.project_home_category_id}
                                            className={`view-projects__all-projects-filter-button ${selectedFilter === area.project_home_category_id ? 'active' : ''}`}
                                            onClick={() => setSelectedFilter(area.project_home_category_id)}
                                        >
                                            {area.project_home_category}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="selected-area-details__projects-list">

                            {selectedAreaProjects.map((project) => {
                                return (
                                    <ProjectsListCard
                                        key={project.id}
                                        project={project}
                                        handleProjectClick={handleProjectClick}
                                        allowDragging={true}
                                    />
                                )
                            })}
                        </div>


                    </div>
                </>
            }
        </>
    );
};

export default HouseView;